import { motion } from 'framer-motion';
import { CheckCircle2, Circle, ChevronRight, FileText, Terminal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlaybookSummary } from '../types/wizard';
import { useState } from 'react';

interface Props {
  playbook: PlaybookSummary;
  currentPhase?: number; // 0-indexed, -1 or undefined = none active (preview mode)
  onPhaseClick?: (index: number) => void;
}

export default function PlaybookPhaseTracker({ playbook, currentPhase, onPhaseClick }: Props) {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">{playbook.icon}</span>
        <h3 className="text-lg font-semibold text-white">{playbook.name}</h3>
        <span className="text-xs text-text-tertiary ml-auto">{playbook.phases.length} phases</span>
      </div>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-px bg-dark-border" />

        {playbook.phases.map((phase, i) => {
          const isCompleted = currentPhase !== undefined && currentPhase > i;
          const isCurrent = currentPhase === i;
          const isExpanded = expandedPhase === i;
          const isPending = currentPhase !== undefined && currentPhase < i;

          return (
            <motion.div
              key={phase.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="relative pl-10 pb-4"
            >
              {/* Phase indicator dot */}
              <div className="absolute left-0 top-1 flex items-center justify-center w-8 h-8 z-10">
                {isCompleted ? (
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                ) : isCurrent ? (
                  <div className="w-6 h-6 rounded-full bg-accent border-2 border-accent flex items-center justify-center">
                    <span className="text-xs font-bold text-white">{i + 1}</span>
                  </div>
                ) : (
                  <Circle className={`w-6 h-6 ${isPending ? 'text-text-tertiary' : 'text-text-secondary'}`} />
                )}
              </div>

              {/* Phase content */}
              <button
                onClick={() => {
                  setExpandedPhase(isExpanded ? null : i);
                  onPhaseClick?.(i);
                }}
                className={`w-full text-left rounded-lg p-3 transition-all ${
                  isCurrent
                    ? 'bg-accent/10 border border-accent/30'
                    : isExpanded
                      ? 'bg-surface-1 border border-dark-border'
                      : 'hover:bg-surface-1/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-xs font-mono ${
                      isCurrent ? 'text-accent' : isCompleted ? 'text-green-400' : 'text-text-tertiary'
                    }`}>
                      Phase {i + 1}
                    </span>
                    <h4 className={`font-medium ${
                      isCurrent ? 'text-white' : isCompleted ? 'text-text-secondary' : isPending ? 'text-text-tertiary' : 'text-text-primary'
                    }`}>
                      {phase.name}
                    </h4>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-text-tertiary transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </div>

                {isCurrent && !isExpanded && (
                  <p className="text-sm text-text-secondary mt-1 line-clamp-2">{phase.description}</p>
                )}
              </button>

              {/* Expanded phase summary */}
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 ml-3 space-y-3"
                >
                  <p className="text-sm text-text-secondary">{phase.description}</p>

                  {/* Quick stats */}
                  <div className="flex flex-wrap gap-3 text-xs text-text-tertiary">
                    {phase.objectiveCount > 0 && (
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {phase.objectiveCount} objective{phase.objectiveCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {phase.deliverableCount > 0 && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {phase.deliverableCount} deliverable{phase.deliverableCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {phase.promptCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Terminal className="w-3 h-3" />
                        {phase.promptCount} prompt{phase.promptCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <Link
                    to={`/playbooks/${playbook.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent-400 transition-colors"
                  >
                    Explore full phase details
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
